/* =========================================================
   LINK BUDGET MICROWAVE

   Perhitungan RSL, fade margin, dan availability (Vigants-Barnett).
   Availability SELALU hasil hitung — tidak pernah diinput user.
========================================================= */

export const ANTENNA_DIAMETERS = [0.3, 0.6, 0.9, 1.2, 1.8, 2.4] as const

export const MODULATIONS = [
  'QPSK',
  '16QAM',
  '32QAM',
  '64QAM',
  '128QAM',
  '256QAM',
  '1024QAM',
] as const

export const CHANNEL_BANDWIDTHS = [7, 14, 28, 40, 56, 80, 112] as const

export type Modulation = (typeof MODULATIONS)[number]

/** RX threshold acuan pada kanal 28 MHz, BER 10⁻⁶ (dBm). */
const THRESHOLD_28MHZ: Record<Modulation, number> = {
  QPSK: -87,
  '16QAM': -81,
  '32QAM': -78,
  '64QAM': -75,
  '128QAM': -72,
  '256QAM': -69,
  '1024QAM': -63,
}

/**
 * Gain antena parabola (dBi) — efisiensi aperture 55%.
 * G = 17.8 + 20·log10(D · f)
 */
export function antennaGain(diameterM: number, frequencyGHz: number): number {
  if (diameterM <= 0 || frequencyGHz <= 0) return 0
  return 17.8 + 20 * Math.log10(diameterM * frequencyGHz)
}

/** RX threshold menyesuaikan lebar kanal: 10·log10(BW / 28). */
export function rxThreshold(
  modulation: Modulation,
  bandwidthMHz: number,
): number {
  const base = THRESHOLD_28MHZ[modulation]
  if (!bandwidthMHz || bandwidthMHz <= 0) return base
  return base + 10 * Math.log10(bandwidthMHz / 28)
}

export interface LinkBudgetInput {
  txPowerNE: number
  txPowerFE: number
  feederLossNE: number
  feederLossFE: number
  gainNE: number
  gainFE: number
  extraLoss: number
  rxThreshold: number
  /** FSPL + difraksi (dB). */
  pathLoss: number
  fspl: number
  diffractionLoss: number
  frequencyGHz: number
  distanceKm: number
}

export type AvailabilityClass = 'four-nines' | 'three-nines' | 'below'

export interface WaterfallSegment {
  label: string
  value: number
  color: string
}

export interface LinkBudgetResult {
  rsl: number
  rxThreshold: number
  fadeMargin: number
  availability: number
  outageMinutesPerYear: number
  availabilityClass: AvailabilityClass
  verdict: 'PASS' | 'MARGINAL' | 'FAIL'
  waterfall: WaterfallSegment[]
}

/**
 * Probabilitas outage multipath — Vigants-Barnett.
 * P = 6·10⁻⁷ · a · b · f · d³ · 10^(−FM/10)
 * a = 1 (terrain rata-rata), b = 0.25 (iklim rata-rata).
 */
function multipathOutage(
  frequencyGHz: number,
  distanceKm: number,
  fadeMargin: number,
): number {
  if (distanceKm <= 0 || frequencyGHz <= 0) return 0
  const p0 = 6.0e-7 * 1 * 0.25 * frequencyGHz * distanceKm ** 3
  return Math.min(p0 * 10 ** (-fadeMargin / 10), 1)
}

export function calculateLinkBudget(
  input: LinkBudgetInput,
): LinkBudgetResult {
  const {
    txPowerNE,
    feederLossNE,
    feederLossFE,
    gainNE,
    gainFE,
    extraLoss,
    rxThreshold: threshold,
    fspl,
    diffractionLoss,
    frequencyGHz,
    distanceKm,
  } = input

  const rsl =
    txPowerNE -
    feederLossNE +
    gainNE -
    fspl -
    diffractionLoss +
    gainFE -
    feederLossFE -
    extraLoss

  const fadeMargin = rsl - threshold

  const outage = multipathOutage(frequencyGHz, distanceKm, fadeMargin)
  const availability = (1 - outage) * 100
  const outageMinutesPerYear = outage * 525600

  const availabilityClass: AvailabilityClass =
    availability >= 99.99
      ? 'four-nines'
      : availability >= 99.9
        ? 'three-nines'
        : 'below'

  const verdict =
    availabilityClass === 'four-nines'
      ? 'PASS'
      : availabilityClass === 'three-nines'
        ? 'MARGINAL'
        : 'FAIL'

  const waterfall: WaterfallSegment[] = [
    { label: 'Tx Power', value: txPowerNE, color: '#38bdf8' },
    { label: 'Gain NE', value: gainNE, color: '#22d3ee' },
    { label: 'FSL', value: -fspl, color: '#f87171' },
    { label: 'Difraksi', value: -diffractionLoss, color: '#fb923c' },
    {
      label: 'Feeder',
      value: -(feederLossNE + feederLossFE + extraLoss),
      color: '#fbbf24',
    },
    { label: 'Gain FE', value: gainFE, color: '#34d399' },
  ]

  return {
    rsl,
    rxThreshold: threshold,
    fadeMargin,
    availability,
    outageMinutesPerYear,
    availabilityClass,
    verdict,
    waterfall,
  }
}

export const AVAILABILITY_CLASS_META: Record<
  AvailabilityClass,
  { chip: string; tone: string; verdictTone: string; accent: string }
> = {
  'four-nines': {
    chip: 'KELAS 99.99%',
    tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
    verdictTone: 'bg-emerald-500/10 border-emerald-400/40 text-emerald-300',
    accent: '#34d399',
  },
  'three-nines': {
    chip: 'KELAS 99.9%',
    tone: 'bg-amber-500/15 text-amber-300 border-amber-400/40',
    verdictTone: 'bg-amber-500/10 border-amber-400/40 text-amber-300',
    accent: '#fbbf24',
  },
  below: {
    chip: 'DI BAWAH 99.9%',
    tone: 'bg-red-500/15 text-red-300 border-red-400/40',
    verdictTone: 'bg-red-500/10 border-red-400/40 text-red-300',
    accent: '#f87171',
  },
}

/** Format downtime menjadi satuan yang mudah dibaca. */
export function formatDowntime(minutesPerYear: number): string {
  if (minutesPerYear < 1) return `${Math.round(minutesPerYear * 60)} detik/tahun`
  if (minutesPerYear < 120) return `${Math.round(minutesPerYear)} menit/tahun`
  if (minutesPerYear < 2880)
    return `${(minutesPerYear / 60).toFixed(1)} jam/tahun`
  return `${(minutesPerYear / 1440).toFixed(1)} hari/tahun`
}
